import * as React from "react"
import { cn } from "@volca/ui/lib/utils"

export type TableVariant = "default" | "card"

/**
 * `card` draws the body as one raised card, rounded and bordered,
 * with the header and footer on the surface around it: put it in a `Frame`
 * (`<Frame><Table variant="card">…</Table></Frame>`) for a list of records.
 *
 * `scroll` makes the table scroll inside its own box rather than the page:
 * the header and footer stay put, and the table fills the height its parent
 * gives it (a flex column with `min-h-0`).
 */
function Table({
  className,
  variant = "default",
  scroll = false,
  ...props
}: React.ComponentProps<"table"> & {
  variant?: TableVariant
  scroll?: boolean
}) {
  return (
    <div
      data-slot="table-container"
      data-variant={variant}
      data-scroll={scroll || undefined}
      className={cn(
        "relative w-full overflow-x-auto",
        scroll && "min-h-0 flex-1 overflow-y-auto overscroll-contain"
      )}
    >
      <table
        data-slot="table"
        className={cn(
          "w-full caption-bottom text-sm in-data-[variant=card]:border-separate in-data-[variant=card]:border-spacing-0",
          className
        )}
        {...props}
      />
    </div>
  )
}

function TableHeader({ className, ...props }: React.ComponentProps<"thead">) {
  return (
    <thead
      data-slot="table-header"
      className={cn(
        "[&_tr]:border-b in-data-[variant=card]:[&_tr]:border-0",
        className
      )}
      {...props}
    />
  )
}

// In a card table each cell carries the card's surface and the outer cells its
// edges, so the rounded corners survive `border-separate`.
const CARD_BODY = [
  "relative in-data-[variant=card]:rounded-lg in-data-[variant=card]:shadow-xs/5",
  "in-data-[variant=card]:before:pointer-events-none in-data-[variant=card]:before:absolute in-data-[variant=card]:before:inset-px in-data-[variant=card]:before:rounded-[calc(var(--radius-lg)-1px)] in-data-[variant=card]:before:shadow-[0_1px_rgb(0_0_0/4%)] dark:in-data-[variant=card]:before:shadow-[0_-1px_rgb(255_255_255/8%)]",
  "in-data-[variant=card]:*:[tr]:border-0",
  "in-data-[variant=card]:*:[tr]:*:[td]:border-b in-data-[variant=card]:*:[tr]:*:[td]:border-line in-data-[variant=card]:*:[tr]:*:[td]:bg-surface-raised",
  "in-data-[variant=card]:*:[tr]:first:*:[td]:border-t",
  "in-data-[variant=card]:*:[tr]:*:[td]:first:border-s in-data-[variant=card]:*:[tr]:*:[td]:last:border-e",
  "in-data-[variant=card]:*:[tr]:first:*:[td]:first:rounded-ss-lg in-data-[variant=card]:*:[tr]:first:*:[td]:last:rounded-se-lg",
  "in-data-[variant=card]:*:[tr]:last:*:[td]:first:rounded-es-lg in-data-[variant=card]:*:[tr]:last:*:[td]:last:rounded-ee-lg",
  "in-data-[variant=card]:*:[tr]:hover:*:[td]:bg-neutral-subtle-hovered",
  "in-data-[variant=card]:*:[tr]:data-[state=selected]:*:[td]:bg-selected",
]

function TableBody({ className, ...props }: React.ComponentProps<"tbody">) {
  return (
    <tbody
      data-slot="table-body"
      className={cn("[&_tr:last-child]:border-0", CARD_BODY, className)}
      {...props}
    />
  )
}

function TableFooter({ className, ...props }: React.ComponentProps<"tfoot">) {
  return (
    <tfoot
      data-slot="table-footer"
      className={cn(
        "border-t bg-neutral font-medium [&>tr]:last:border-b-0",
        "in-data-[variant=card]:border-0 in-data-[variant=card]:bg-transparent in-data-[variant=card]:font-normal in-data-[variant=card]:text-subtle",
        className
      )}
      {...props}
    />
  )
}

function TableRow({ className, ...props }: React.ComponentProps<"tr">) {
  return (
    <tr
      data-slot="table-row"
      className={cn(
        "border-b transition-colors not-in-data-[variant=card]:hover:bg-neutral-subtle-hovered has-aria-expanded:bg-neutral-subtle-pressed not-in-data-[variant=card]:data-[state=selected]:bg-selected data-[state=selected]:text-brand",
        className
      )}
      {...props}
    />
  )
}

function TableHead({ className, ...props }: React.ComponentProps<"th">) {
  return (
    <th
      data-slot="table-head"
      className={cn(
        "h-10 px-2 text-left align-middle font-medium whitespace-nowrap text-ink [&:has([role=checkbox])]:pr-0",
        "in-data-[variant=card]:h-9 in-data-[variant=card]:px-3 in-data-[variant=card]:text-subtle",
        "in-data-[scroll]:sticky in-data-[scroll]:top-0 in-data-[scroll]:z-10 in-data-[scroll]:bg-surface-sunken",
        className
      )}
      {...props}
    />
  )
}

function TableCell({ className, ...props }: React.ComponentProps<"td">) {
  return (
    <td
      data-slot="table-cell"
      className={cn(
        "p-2 align-middle whitespace-nowrap [&:has([role=checkbox])]:pr-0",
        "in-data-[variant=card]:bg-clip-padding in-data-[variant=card]:px-3 in-data-[variant=card]:py-2 in-data-[variant=card]:in-data-[slot=table-footer]:py-2.5",
        "in-data-[variant=card]:first:ps-[calc(--spacing(3)-1px)] in-data-[variant=card]:last:pe-[calc(--spacing(3)-1px)]",
        "in-data-[scroll]:in-data-[slot=table-footer]:sticky in-data-[scroll]:in-data-[slot=table-footer]:bottom-0 in-data-[scroll]:in-data-[slot=table-footer]:z-10 in-data-[scroll]:in-data-[slot=table-footer]:bg-surface-sunken",
        className
      )}
      {...props}
    />
  )
}

function TableCaption({
  className,
  ...props
}: React.ComponentProps<"caption">) {
  return (
    <caption
      data-slot="table-caption"
      className={cn("mt-4 text-sm leading-[1.5] text-subtle", className)}
      {...props}
    />
  )
}

export {
  Table,
  TableHeader,
  TableBody,
  TableFooter,
  TableHead,
  TableRow,
  TableCell,
  TableCaption,
}
